/**
 * Helpers for the Class Dashboard (roster, attendance, CSV export).
 */

export function studentTypeLabel(rsvp) {
  const via = String(rsvp?.redemption_via || "").toUpperCase();
  const source = String(rsvp?.source || "").toUpperCase();
  if (via === "TIME_PASS" || via === "FREE_RULE" || via === "COMMERCE_OFF") return "Member";
  if (via === "CREDIT") return "Credit";
  if (via === "HOST" || via === "OFFER" || source === "HOST") return "Drop-in";
  if (via) return "Drop-in";
  return "Drop-in";
}

export function rosterSummary(rsvps = []) {
  const expected = rsvps.length;
  const checkedIn = rsvps.filter((r) => r.status === "ATTENDED").length;
  return {
    expected,
    checkedIn,
    remaining: Math.max(0, expected - checkedIn),
  };
}

export function filterRosterByQuery(rsvps = [], query = "") {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return rsvps;
  return rsvps.filter((r) => {
    const name = String(r.name || "").toLowerCase();
    const role = String(r.role || "").toLowerCase();
    const type = studentTypeLabel(r).toLowerCase();
    return name.includes(q) || role.includes(q) || type.includes(q);
  });
}

export function formatSessionDatePill(iso) {
  if (!iso) return "";
  try {
    const date = new Date(iso);
    const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
    const month = date.toLocaleDateString("en-US", { month: "short" });
    return `${weekday}, ${month} ${date.getDate()}`;
  } catch {
    return "";
  }
}

export function formatCheckInTime(date = new Date()) {
  try {
    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "";
  }
}

export function formatClassScheduleLabel(recurrence = {}) {
  const days = (recurrence.days || []).join(", ");
  const time = recurrence.time || "";
  if (!days && !time) return "Schedule not set";
  if (days && time) return `Every ${days}, ${time}`;
  return [days ? `Every ${days}` : null, time].filter(Boolean).join(", ");
}

export function buildRosterCsv(rsvps = []) {
  const header = ["Name", "Role", "Type", "Status"];
  const rows = rsvps.map((r) => [
    r.name || "",
    r.role || "",
    studentTypeLabel(r),
    r.status || "",
  ]);
  const escape = (cell) => {
    const s = String(cell ?? "");
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  return [header, ...rows].map((row) => row.map(escape).join(",")).join("\n");
}

export function downloadRosterCsv(rsvps, filename = "class-roster.csv") {
  const csv = buildRosterCsv(rsvps);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function buildClassShareText({ className, scheduleLabel, locationLabel, link }) {
  const lines = [`*${className || "Class"}*`];
  if (scheduleLabel) lines.push(scheduleLabel);
  if (locationLabel) lines.push(locationLabel);
  if (link) lines.push("", `Join here: ${link}`);
  return lines.join("\n");
}

export const CLASS_DASHBOARD_TABS = [
  { key: "roster", label: "Roster & Attendance", panelId: "class-dash-panel-roster" },
  { key: "broadcast", label: "Reach out", panelId: "class-dash-panel-broadcast" },
  { key: "edit", label: "Edit Week", panelId: "class-dash-panel-edit" },
];

export function normalizeClassDashboardTab(tab) {
  if (tab === "broadcast" || tab === "edit" || tab === "roster") return tab;
  return "roster";
}
